import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, TextInput, ActivityIndicator, Alert, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';
import { firestoreService } from '../services/firestoreService';
import { useAppStore } from '../store/useAppStore';

/**
 * BLOQUEAR Y REPORTAR A LA OTRA PERSONA DE UN CHAT.
 *
 * ═══ POR QUÉ EXISTE ═══
 *
 * La política de contenido generado por usuarios de Google Play exige que, en
 * un chat de uno a uno, cualquiera pueda **bloquear** a la otra persona y
 * **reportarla**, dentro de la app y sin salir a buscar un correo de soporte.
 * Go Safely tenía tres chats (viaje, pedido de comida y negociación de Rent
 * Car) y ninguno de los tres tenía ni lo uno ni lo otro. Es de las cosas por
 * las que Play rechaza una app, y esta app ya fue rechazada una vez.
 *
 * ═══ CÓMO FUNCIONA ═══
 *
 * Bloquear escribe `users/{yo}/bloqueados/{elOtro}`. A partir de ahí las
 * REGLAS de Firestore deniegan los mensajes en las dos direcciones: el
 * bloqueado no me escribe, y yo tampoco le escribo a él (un bloqueo que solo
 * corta un sentido deja media conversación viva). No se borra nada de lo ya
 * dicho: el historial es prueba si hay que reclamar.
 *
 * Reportar escribe en `reportes_usuarios` con el contexto (qué chat, qué
 * viaje/pedido) para que el administrador lo vea, y ofrece bloquear en el
 * mismo gesto, que es lo que uno quiere cuando algo se pone feo.
 *
 * Este componente es uno solo para los tres chats a propósito: tres copias de
 * esta pantalla acabarían siendo tres comportamientos distintos.
 */

type Props = {
  /** El uid de la otra persona. Sin esto no se puede bloquear a nadie. */
  otroUid: string | null | undefined;
  /** Cómo llamarla en los avisos ("el chofer", "María"…). */
  otroNombre?: string | null;
  /** De qué conversación viene: 'VIAJE' | 'PEDIDO' | 'RENT_CAR'. */
  contexto: 'VIAJE' | 'PEDIDO' | 'RENT_CAR';
  /** El id del viaje / pedido / negociación, para que el reporte se pueda mirar. */
  referenciaId?: string | null;
  /** Se avisa al padre cuando cambia el bloqueo, para que cierre el teclado. */
  onCambioDeBloqueo?: (bloqueado: boolean) => void;
};

export default function AccionesDeChat({
  otroUid,
  otroNombre,
  contexto,
  referenciaId,
  onCambioDeBloqueo,
}: Props) {
  const colors = useThemeColors();
  // El uid propio se lee aquí y se le pasa al servicio: el servicio NO importa
  // `authBridge` a propósito (authBridge ya importa el servicio y el ciclo
  // deja a uno de los dos a medio inicializar en el arranque).
  const yo = useAppStore((s) => s.userFirebaseUid);
  const [abierto, setAbierto] = useState(false);
  const [bloqueado, setBloqueado] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);
  const nombre = String(otroNombre ?? '').trim() || 'esta persona';

  useEffect(() => {
    let vivo = true;
    if (!otroUid || !yo) return;
    firestoreService
      .loBloquee(String(yo), String(otroUid))
      .then((r) => { if (vivo) setBloqueado(!!r); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [otroUid, yo]);

  const avisar = (titulo: string, cuerpo: string) => {
    // `Alert.alert` no pinta nada en RN-web y esta pantalla también corre ahí.
    if (Platform.OS === 'web') {
      // eslint-disable-next-line no-alert
      window.alert(`${titulo}\n\n${cuerpo}`);
      return;
    }
    Alert.alert(titulo, cuerpo);
  };

  const bloquear = async () => {
    if (!otroUid) return;
    setTrabajando(true);
    try {
      await firestoreService.bloquearUsuario(String(yo), String(otroUid), contexto, referenciaId ?? null);
      setBloqueado(true);
      setAbierto(false);
      onCambioDeBloqueo?.(true);
      avisar(
        'Bloqueada la conversación',
        `Ni ${nombre} podrá escribirte ni tú a ella. Lo que ya se dijo se conserva por si hay que revisarlo.`
      );
    } catch (e: any) {
      avisar('No se pudo bloquear', e?.message ?? 'Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setTrabajando(false);
    }
  };

  const desbloquear = async () => {
    if (!otroUid) return;
    setTrabajando(true);
    try {
      await firestoreService.desbloquearUsuario(String(yo), String(otroUid));
      setBloqueado(false);
      setAbierto(false);
      onCambioDeBloqueo?.(false);
      avisar('Desbloqueada', `${nombre} vuelve a poder escribirte.`);
    } catch (e: any) {
      avisar('No se pudo desbloquear', e?.message ?? 'Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setTrabajando(false);
    }
  };

  const reportar = async (tambienBloquear: boolean) => {
    if (!otroUid) return;
    setTrabajando(true);
    try {
      await firestoreService.reportarUsuario({
        yo: String(yo),
        otroUid: String(otroUid),
        otroNombre: nombre,
        contexto,
        referenciaId: referenciaId ?? null,
        motivo: motivo.trim(),
      });
      if (tambienBloquear && !bloqueado) {
        await firestoreService.bloquearUsuario(String(yo), String(otroUid), contexto, referenciaId ?? null);
        setBloqueado(true);
        onCambioDeBloqueo?.(true);
      }
      setPidiendoMotivo(false);
      setAbierto(false);
      setMotivo('');
      avisar(
        'Reporte enviado',
        tambienBloquear
          ? 'Lo va a revisar una persona de Go Safely, y mientras tanto la conversación queda bloqueada.'
          : 'Lo va a revisar una persona de Go Safely.'
      );
    } catch (e: any) {
      avisar('No se pudo enviar el reporte', e?.message ?? 'Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setTrabajando(false);
    }
  };

  if (!otroUid || !yo) return null;

  return (
    <>
      <TouchableOpacity
        onPress={() => setAbierto(true)}
        accessibilityRole="button"
        accessibilityLabel={`Opciones de la conversación con ${nombre}`}
        style={{ padding: 8, marginLeft: 4 }}
      >
        <Ionicons name="ellipsis-vertical" size={22} color={colors.text} />
      </TouchableOpacity>

      <Modal visible={abierto} transparent animationType="fade" onRequestClose={() => setAbierto(false)}>
        <TouchableOpacity
          activeOpacity={1}
          onPress={() => { if (!trabajando) { setAbierto(false); setPidiendoMotivo(false); } }}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}
        >
          <TouchableOpacity activeOpacity={1} onPress={() => {}} style={{ backgroundColor: colors.card, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 34 }}>
            <Text style={{ color: colors.text, fontSize: 17, fontWeight: '800', marginBottom: 4 }}>
              {`Conversación con ${nombre}`}
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 16 }}>
              Si te falta al respeto, te pide algo fuera de la app o te incomoda, bloquéala o repórtala. Lo revisa una persona.
            </Text>

            {pidiendoMotivo ? (
              <>
                <TextInput
                  value={motivo}
                  onChangeText={setMotivo}
                  placeholder="¿Qué pasó? (opcional, pero ayuda)"
                  placeholderTextColor={colors.textSecondary}
                  multiline
                  maxLength={500}
                  style={{
                    minHeight: 80,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderRadius: 12,
                    padding: 12,
                    color: colors.text,
                    textAlignVertical: 'top',
                    marginBottom: 14,
                  }}
                />
                <TouchableOpacity
                  disabled={trabajando}
                  onPress={() => reportar(true)}
                  style={{ backgroundColor: '#FF3B30', borderRadius: 12, paddingVertical: 14, alignItems: 'center', marginBottom: 10 }}
                >
                  {trabajando ? (
                    <ActivityIndicator color="#FFF" />
                  ) : (
                    <Text style={{ color: '#FFF', fontWeight: '800' }}>Reportar y bloquear</Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={trabajando}
                  onPress={() => reportar(false)}
                  style={{ borderWidth: 1.5, borderColor: colors.border, borderRadius: 12, paddingVertical: 13, alignItems: 'center' }}
                >
                  <Text style={{ color: colors.text, fontWeight: '700' }}>Solo reportar</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity
                  disabled={trabajando}
                  onPress={bloqueado ? desbloquear : bloquear}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    borderWidth: 1.5,
                    borderColor: bloqueado ? colors.border : '#FF3B30',
                    backgroundColor: bloqueado ? 'transparent' : 'rgba(255,59,48,0.08)',
                    borderRadius: 12,
                    paddingVertical: 14,
                    marginBottom: 10,
                  }}
                >
                  {trabajando ? (
                    <ActivityIndicator color={bloqueado ? colors.text : '#FF3B30'} />
                  ) : (
                    <>
                      <Ionicons
                        name={bloqueado ? 'lock-open-outline' : 'ban-outline'}
                        size={18}
                        color={bloqueado ? colors.text : '#FF3B30'}
                      />
                      <Text style={{ color: bloqueado ? colors.text : '#FF3B30', fontWeight: '800' }}>
                        {bloqueado ? 'Desbloquear' : `Bloquear a ${nombre}`}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  disabled={trabajando}
                  onPress={() => setPidiendoMotivo(true)}
                  style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderColor: colors.border, borderRadius: 12, paddingVertical: 13, marginBottom: 10 }}
                >
                  <Ionicons name="flag-outline" size={18} color={colors.text} />
                  <Text style={{ color: colors.text, fontWeight: '700' }}>Reportar</Text>
                </TouchableOpacity>

                <TouchableOpacity onPress={() => setAbierto(false)} style={{ paddingVertical: 12, alignItems: 'center' }}>
                  <Text style={{ color: colors.textSecondary, fontWeight: '700' }}>Cancelar</Text>
                </TouchableOpacity>
              </>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

/**
 * El aviso de que la conversación está bloqueada, para poner encima de la caja
 * de texto. Se exporta aparte para que cada chat lo coloque donde le cuadre sin
 * repetir el texto (ni contradecirse).
 */
export function AvisoDeBloqueo({ bloqueado }: { bloqueado: boolean }) {
  const colors = useThemeColors();
  if (!bloqueado) return null;
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgba(255,59,48,0.10)',
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,59,48,0.35)',
        paddingVertical: 12,
        paddingHorizontal: 14,
      }}
    >
      <Ionicons name="ban" size={16} color="#FF3B30" />
      <Text style={{ color: colors.text, fontSize: 13, flex: 1 }}>
        Bloqueaste esta conversación. Nadie puede escribir. Puedes desbloquearla desde el menú de arriba.
      </Text>
    </View>
  );
}
